import React, { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { CaptionStyles } from "../../../types";
import { CaptionStylePreset } from "../../../types/caption-styles";
import { BUILT_IN_PRESETS } from "../../../templates/caption-templates";
import { Input } from "../../ui/input";

interface CaptionPresetSelectorProps {
  currentStyles: CaptionStyles;
  onApplyPreset: (styles: CaptionStyles) => void;
  userPresets: CaptionStylePreset[];
  onSavePreset: (name: string, styles: CaptionStyles) => Promise<void>;
  onDeletePreset: (presetId: string) => Promise<void>;
}

/**
 * CaptionPresetSelector Component
 *
 * Displays a 3-column grid of preset cards with live previews.
 * Includes built-in presets and user-saved custom presets.
 */
export const CaptionPresetSelector: React.FC<CaptionPresetSelectorProps> = ({
  currentStyles,
  onApplyPreset,
  userPresets,
  onSavePreset,
  onDeletePreset,
}) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const allPresets = [...BUILT_IN_PRESETS, ...userPresets];

  const handleSavePreset = async () => {
    if (!presetName.trim()) return;
    setIsSaving(true);
    try {
      await onSavePreset(presetName.trim(), currentStyles);
      setPresetName("");
      setShowSaveDialog(false);
    } finally {
      setIsSaving(false);
    }
  };

  const renderPresetPreview = (preset: CaptionStylePreset) => {
    const styles = preset.styles;
    return (
      <div className="flex flex-col items-center justify-center gap-0.5">
        <span
          style={{
            color: styles.color,
            fontFamily: styles.fontFamily,
            fontSize: "0.875rem",
            lineHeight: "1.3",
            fontWeight: styles.fontWeight,
            textShadow: styles.textShadow,
            letterSpacing: styles.letterSpacing,
          }}
        >
          Hello
        </span>
        <span
          style={{
            backgroundColor: styles.highlightStyle?.backgroundColor,
            color: styles.highlightStyle?.color || styles.color,
            padding: "2px 8px",
            borderRadius: styles.highlightStyle?.borderRadius || "4px",
            fontFamily: styles.fontFamily,
            fontSize: "0.875rem",
            lineHeight: "1.3",
            fontWeight: styles.highlightStyle?.fontWeight || styles.fontWeight,
            textShadow: styles.highlightStyle?.textShadow,
          }}
        >
          World
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-4 rounded-md bg-sidebar p-3 border min-w-0 w-full max-w-full">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-medium leading-none text-foreground">
          Presets
        </h5>
        <button
          onClick={() => setShowSaveDialog(true)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Save
        </button>
      </div>

      {/* Save Preset Dialog */}
      {showSaveDialog && (
        <div className="flex items-center gap-2 p-2 bg-background rounded-md border">
          <Input
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            placeholder="Preset name..."
            className="h-8 text-xs flex-1"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSavePreset();
              if (e.key === "Escape") setShowSaveDialog(false);
            }}
            autoFocus
          />
          <button
            onClick={handleSavePreset}
            disabled={!presetName.trim() || isSaving}
            className="px-2 py-1 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "..." : "Save"}
          </button>
          <button
            onClick={() => {
              setShowSaveDialog(false);
              setPresetName("");
            }}
            className="p-1 rounded-md hover:bg-accent"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Preset Grid - 3 columns */}
      <div className="grid grid-cols-3 gap-3">
        {allPresets.map((preset) => (
          <div key={preset.id} className="relative group">
            <button
              onClick={() => onApplyPreset(preset.styles)}
              className="w-full aspect-square rounded-lg border bg-card/60 hover:border-primary/50 hover:bg-card transition-all overflow-hidden flex items-center justify-center"
            >
              {renderPresetPreview(preset)}
            </button>
            <div className="text-xs text-center text-muted-foreground mt-1.5 truncate">
              {preset.name}
            </div>
            {/* Delete button for user presets */}
            {!preset.isBuiltIn && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeletePreset(preset.id);
                }}
                className="absolute -top-1.5 -right-1.5 p-1 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
